import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import LegalDocumentCard from './components/LegalDocumentCard';
import Footer from '../homepage-sales-landing/components/Footer';

const LegalPagesHub = () => {
  const legalDocuments = [
    {
      title: 'Privacy Policy',
      description: 'Learn how we collect, use, and protect your personal information and data.',
      icon: 'Shield',
      path: '/privacy-policy',
      lastUpdated: 'December 8, 2025',
      color: 'text-blue-600'
    },
    {
      title: 'Terms of Service',
      description: 'Review the terms and conditions governing your use of AuthorPreneur AI platform.',
      icon: 'FileText',
      path: '/terms-of-service',
      lastUpdated: 'December 8, 2025',
      color: 'text-green-600'
    },
    {
      title: 'Cookie Policy',
      description: 'Understand how we use cookies and similar technologies on our platform.',
      icon: 'Cookie',
      path: '/cookie-policy',
      lastUpdated: 'December 8, 2025',
      color: 'text-orange-600'
    },
    {
      title: 'GDPR Compliance',
      description: 'View our commitment to GDPR compliance and your data protection rights.',
      icon: 'Lock',
      path: '/gdpr-compliance',
      lastUpdated: 'December 8, 2025',
      color: 'text-purple-600'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Legal Pages Hub - AuthorPreneur AI</title>
        <meta name="description" content="Access all legal documentation and policies for AuthorPreneur AI platform" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card">
          <div className="container mx-auto px-4 py-6">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="BookOpen" size={24} color="var(--color-primary)" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">
                AuthorPreneur AI
              </span>
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12 lg:py-16">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-6">
              <Icon name="Scale" size={32} color="var(--color-primary)" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              Legal Documentation
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive legal information and policies for AuthorPreneur AI platform users
            </p>
          </div>

          {/* Legal Documents Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {legalDocuments?.map((doc) => (
              <LegalDocumentCard key={doc?.path} document={doc} />
            ))}
          </div>

          {/* Company Information */}
          <div className="bg-card border border-border rounded-2xl p-8 mb-12">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon name="Building2" size={24} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                  Company Information
                </h2>
                <p className="text-muted-foreground">
                  Legal entity operating AuthorPreneur AI platform
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Company Name</h3>
                <p className="text-muted-foreground">Entrepreneur Power Network, LLC</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Legal Address</h3>
                <p className="text-muted-foreground">
                  813 N. Main Street, Suite 502<br />
                  McAllen, TX 78501
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Contact Email</h3>
                <a 
                  href="mailto:success@entrepreneurpowernetwork.com" 
                  className="text-primary hover:underline"
                >
                  success@entrepreneurpowernetwork.com
                </a>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Platform</h3>
                <p className="text-muted-foreground">AuthorPreneur AI</p>
              </div>
            </div>
          </div>

          {/* Search Functionality */}
          <div className="bg-muted/50 border border-border rounded-2xl p-8 mb-12">
            <div className="flex items-center space-x-4 mb-4">
              <Icon name="Search" size={24} color="var(--color-muted-foreground)" />
              <h3 className="text-xl font-heading font-semibold text-foreground">
                Search Legal Terms
              </h3>
            </div>
            <p className="text-muted-foreground mb-4">
              Looking for specific information in our legal documents? Use the search function within each policy page or contact us directly.
            </p>
            <Link 
              to="mailto:success@entrepreneurpowernetwork.com"
              className="inline-flex items-center text-primary hover:underline"
            >
              Contact Legal Team
              <Icon name="ArrowRight" size={16} className="ml-2" />
            </Link>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-1">
                  Legal Disclaimer
                </h3>
                <p className="text-sm text-muted-foreground">
                  These legal documents were last updated on December 8, 2025. We may update our policies periodically to reflect changes in our practices or legal requirements. Users will be notified of significant changes via email or platform notifications.
                </p>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default LegalPagesHub;